import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';

interface PaymentFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (subscriptionData: any) => void;
  selectedPlan: 'basic' | 'enterprise' | null;
  apiUrl: string;
}

interface PaymentFormData {
  cardNumber: string;
  cardholderName: string;
  expirationMonth: string;
  expirationYear: string;
  cvv: string;
}

const RapydPaymentForm: React.FC<PaymentFormProps> = ({
  open,
  onClose,
  onSuccess,
  selectedPlan,
  apiUrl
}) => {
  const [paymentFormData, setPaymentFormData] = useState<PaymentFormData>({
    cardNumber: '',
    cardholderName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: ''
  });
  
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // 3DS verification states
  const [isRedirecting3DS, setIsRedirecting3DS] = useState(false);
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
  const [pendingCustomerId, setPendingCustomerId] = useState<string | null>(null);
  const [pendingPaymentMethodId, setPendingPaymentMethodId] = useState<string | null>(null);

  // Handle 3DS verification completion
  const handle3DSCompletion = useCallback(async () => {
    // Get IDs from state or localStorage
    const customerId = pendingCustomerId || localStorage.getItem('pendingCustomerId');
    const paymentMethodId = pendingPaymentMethodId || localStorage.getItem('pendingPaymentMethodId');
    
    if (!customerId || !paymentMethodId) {
      console.error('Missing customer ID or payment method ID for verification');
      setPaymentError('Verification failed: missing payment information');
      return;
    }
    
    try {
      setIsProcessingPayment(true);
      
      console.log('Verifying 3DS completion with:', { customerId, paymentMethodId });
      
      const response = await axios.post(
        `${apiUrl}/api/payments/verify-3ds`,
        {
          customerId,
          paymentMethodId
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Clear stored data
        localStorage.removeItem('pendingCustomerId');
        localStorage.removeItem('pendingPaymentMethodId');
        localStorage.removeItem('pendingPlan');
        
        setPaymentSuccess(true);
        onSuccess(response.data.subscription);
        
        // Reset 3DS state
        setPendingCustomerId(null);
        setPendingPaymentMethodId(null);
        setVerificationUrl(null);
        
        // Close payment dialog after successful payment
        setTimeout(() => {
          onClose();
          setPaymentSuccess(false);
        }, 2000);
      } else {
        setPaymentError('3DS verification failed. Please try again.');
      }
    } catch (error: any) {
      console.error('3DS verification error:', error);
      setPaymentError(
        error.response?.data?.message || 
        '3DS verification failed. Please try again.'
      );
    } finally {
      setIsProcessingPayment(false);
      setIsRedirecting3DS(false);
    }
  }, [pendingCustomerId, pendingPaymentMethodId, apiUrl, onSuccess, onClose]);

  // Check for 3DS verification return
  useEffect(() => {
    const checkFor3DSReturn = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      
      // When returning from 3DS, the URL will have payment=success
      if (paymentStatus === 'success' && pendingCustomerId && pendingPaymentMethodId) {
        console.log('Detected return from 3DS verification, verifying payment...');
        
        // Clear URL parameters without page refresh
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Verify the payment
        handle3DSCompletion();
      }
    };
    
    if (open) {
      checkFor3DSReturn();
    }
  }, [pendingCustomerId, pendingPaymentMethodId, handle3DSCompletion, open]);

  // Process payment
  const handleProcessPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPlan) {
      setPaymentError('Please select a plan first');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError(null);
    
    try {
      const response = await axios.post(
        `${apiUrl}/api/payments/process`,
        {
          cardDetails: paymentFormData,
          plan: selectedPlan
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Check if 3DS verification is required
        if (response.data.requires3DS && response.data.redirectUrl) {
          // Save verification data to localStorage for persistence through redirect
          localStorage.setItem('pendingCustomerId', response.data.customerId);
          localStorage.setItem('pendingPaymentMethodId', response.data.paymentMethodId);
          localStorage.setItem('pendingPlan', selectedPlan || '');
          
          setVerificationUrl(response.data.redirectUrl);
          setPendingCustomerId(response.data.customerId);
          setPendingPaymentMethodId(response.data.paymentMethodId);
          setIsRedirecting3DS(true);
          
          // Open the 3DS verification in a new window
          window.open(response.data.redirectUrl, '_self');
          return;
        }
        
        // Standard success flow
        setPaymentSuccess(true);
        onSuccess(response.data.subscription);
        
        // Close payment dialog after successful payment
        setTimeout(() => {
          onClose();
          setPaymentSuccess(false);
        }, 2000);
      }
    } catch (error: any) {
      console.error('Payment processing error:', error);
      setPaymentError(
        error.response?.data?.message || 
        'Payment processing failed. Please try again.'
      );
    } finally {
      if (!isRedirecting3DS) {
        setIsProcessingPayment(false);
      }
    }
  };

  // Initialize state from localStorage on component mount
  useEffect(() => {
    const storedCustomerId = localStorage.getItem('pendingCustomerId');
    const storedPaymentMethodId = localStorage.getItem('pendingPaymentMethodId');
    
    if (storedCustomerId) {
      setPendingCustomerId(storedCustomerId);
    }
    
    if (storedPaymentMethodId) {
      setPendingPaymentMethodId(storedPaymentMethodId);
    }
  }, []);

  // Handle form input changes
  const handlePaymentInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPaymentFormData({
      ...paymentFormData,
      [name]: value
    });
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    if (!isProcessingPayment && !isRedirecting3DS) {
      setPaymentError(null);
      setPaymentSuccess(false);
      setPaymentFormData({
        cardNumber: '',
        cardholderName: '',
        expirationMonth: '',
        expirationYear: '',
        cvv: ''
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
      <DialogTitle>
        Payment for {selectedPlan === 'basic' ? 'Basic' : 'Enterprise'} Plan
      </DialogTitle>
      <DialogContent>
        {paymentSuccess ? (
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1">
              Your subscription has been activated.
            </Typography>
          </Box>
        ) : isRedirecting3DS ? (
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Redirecting to 3D Secure Verification...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please complete the verification in the new window.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleProcessPayment} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Subscription Price: ${selectedPlan === 'basic' ? '20' : '40'}/month
            </Typography>
            
            {paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {paymentError}
              </Alert>
            )}
            
            <TextField
              required
              fullWidth
              label="Cardholder Name"
              name="cardholderName"
              value={paymentFormData.cardholderName}
              onChange={handlePaymentInputChange}
              margin="normal"
            />
            
            <TextField
              required
              fullWidth
              label="Card Number"
              name="cardNumber"
              value={paymentFormData.cardNumber}
              onChange={handlePaymentInputChange}
              margin="normal"
              placeholder="4111111111111111"
              inputProps={{ 
                maxLength: 16,
                pattern: "[0-9]{16}" 
              }}
              helperText="Use 4111111111111111 for testing"
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  label="Expiration Month"
                  name="expirationMonth"
                  value={paymentFormData.expirationMonth}
                  onChange={handlePaymentInputChange}
                  margin="normal"
                  placeholder="MM"
                  inputProps={{ 
                    maxLength: 2,
                    pattern: "[0-9]{2}" 
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  required
                  fullWidth
                  label="Expiration Year"
                  name="expirationYear"
                  value={paymentFormData.expirationYear}
                  onChange={handlePaymentInputChange}
                  margin="normal"
                  placeholder="YY"
                  inputProps={{ 
                    maxLength: 2,
                    pattern: "[0-9]{2}" 
                  }}
                />
              </Grid>
            </Grid>
            
            <TextField
              required
              fullWidth
              label="CVV"
              name="cvv"
              value={paymentFormData.cvv}
              onChange={handlePaymentInputChange}
              margin="normal"
              inputProps={{ 
                maxLength: 3,
                pattern: "[0-9]{3}" 
              }}
            />
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" display="block" gutterBottom>
                Powered by Rapyd Payments
              </Typography>
              <Typography variant="caption" display="block" gutterBottom color="text.secondary">
                Secure payment processing with 3D Secure
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      {!paymentSuccess && !isRedirecting3DS && (
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleProcessPayment}
            disabled={isProcessingPayment}
          >
            {isProcessingPayment ? 'Processing...' : 'Pay Now'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default RapydPaymentForm; 